import type {
  GetContractRevenueScheduleReportResponsePayload,
  UpsertRevenueRecognitionOverrideRequestPayload,
  UpsertRevenueRecognitionOverrideResponsePayload,
  DeleteRevenueRecognitionOverrideResponsePayload,
} from '@vy/api-definition';
import type {
  CustomerRevenueReport,
  CustomerRevenueReportItem,
  RevenuePerMonthBreakdown,
  RevenueRecognitionTypeOverride,
  YearlyRevenue,
} from '@vy/types';
import { MONTHS } from '@vy/types';
import type { UseMutationOptions } from 'react-query';
import { useMutation, useQueryClient } from 'react-query';
import { getPrivateApi } from '../../services/api';
import { queryKeys } from '../../services/query-key/query-keys';

type OverrideRecord = { date: string; value: number };
type ContractReport = GetContractRevenueScheduleReportResponsePayload;

function rebuildYearlyRevenueWithRevenueOverride(
  existing: YearlyRevenue,
  revenueOverride: OverrideRecord[],
): YearlyRevenue {
  const overrideByMonthKey = new Map<string, number>();
  for (const { date, value } of revenueOverride) {
    const key = date.slice(0, 7); // YYYY-MM
    overrideByMonthKey.set(key, (overrideByMonthKey.get(key) ?? 0) + value);
  }

  const monthKeys = new Set<string>();
  for (const yearStr of Object.keys(existing)) {
    const months = existing[yearStr];
    for (const monthName of Object.keys(months)) {
      const monthIndex = MONTHS.indexOf(monthName as (typeof MONTHS)[number]);
      if (monthIndex >= 0) {
        monthKeys.add(`${yearStr}-${String(monthIndex + 1).padStart(2, '0')}`);
      }
    }
  }
  for (const key of overrideByMonthKey.keys()) monthKeys.add(key);

  const sortedKeys = [...monthKeys].sort();

  const result: YearlyRevenue = {};
  let cumulativeBilled = 0;
  let cumulativeRevenue = 0;

  for (const key of sortedKeys) {
    const [yearStr, monthStr] = key.split('-');
    const monthIndex = Number(monthStr) - 1;
    const monthName = MONTHS[monthIndex] as keyof RevenuePerMonthBreakdown extends never ? string : string;

    const existingBreakdown = existing[yearStr]?.[monthName as keyof typeof existing[string]];
    const billed = existingBreakdown?.scheduledCharge ?? 0;
    const revenue = overrideByMonthKey.get(key) ?? 0;

    cumulativeBilled += billed;
    cumulativeRevenue += revenue;

    result[yearStr] = {
      ...(result[yearStr] ?? {}),
      [monthName]: {
        scheduledCharge: billed,
        revenue,
        deferredRevenue: Math.max(0, cumulativeBilled - cumulativeRevenue),
        unbilledRevenue: Math.max(0, cumulativeRevenue - cumulativeBilled),
      },
    };
  }

  return result;
}

function patchCustomerReportItemForVariant(
  report: CustomerRevenueReport,
  variantId: string,
  next: Partial<CustomerRevenueReportItem>,
): CustomerRevenueReport {
  return {
    ...report,
    reportItems: report.reportItems.map((item) =>
      item.variantId === variantId ? { ...item, ...next } : item,
    ),
  };
}

function patchContractReportItemForVariant(
  report: ContractReport,
  variantId: string,
  next: Partial<CustomerRevenueReportItem>,
): ContractReport {
  return {
    ...report,
    reportItems: report.reportItems.map((item) =>
      item.variantId === variantId ? { ...item, ...next } : item,
    ),
  };
}

function applyOptimisticPatch(
  variantId: string,
  revenueOverride: OverrideRecord[],
  isOverridden: boolean,
  recognitionTypeOverride: RevenueRecognitionTypeOverride | null,
  caches: {
    customer?: CustomerRevenueReport;
    contract?: ContractReport;
  },
): { customer?: CustomerRevenueReport; contract?: ContractReport } {
  const result: { customer?: CustomerRevenueReport; contract?: ContractReport } = {};

  if (caches.customer) {
    const target = caches.customer.reportItems.find((item) => item.variantId === variantId);
    if (target) {
      const patchedYearly = rebuildYearlyRevenueWithRevenueOverride(target.yearlyRevenue, revenueOverride);
      result.customer = patchCustomerReportItemForVariant(caches.customer, variantId, {
        yearlyRevenue: patchedYearly,
        isOverridden,
        recognitionTypeOverride,
      });
    } else {
      result.customer = caches.customer;
    }
  }

  if (caches.contract) {
    const target = caches.contract.reportItems.find((item) => item.variantId === variantId);
    if (target) {
      const patchedYearly = rebuildYearlyRevenueWithRevenueOverride(target.yearlyRevenue, revenueOverride);
      result.contract = patchContractReportItemForVariant(caches.contract, variantId, {
        yearlyRevenue: patchedYearly,
        isOverridden,
        recognitionTypeOverride,
      });
    } else {
      result.contract = caches.contract;
    }
  }

  return result;
}

type MutationSnapshot = {
  previousCustomerReport?: CustomerRevenueReport;
  previousContractReport?: ContractReport;
  userContext?: unknown;
};

export function useUpsertRevenueRecognitionOverride(
  variantId: string,
  contractId?: string,
  options?: UseMutationOptions<
    UpsertRevenueRecognitionOverrideResponsePayload,
    unknown,
    UpsertRevenueRecognitionOverrideRequestPayload
  >,
) {
  const queryClient = useQueryClient();
  const customerKey = queryKeys.customerRevenueReport();
  const contractKey = contractId ? queryKeys.contractRevenueScheduleReport(contractId) : null;

  return useMutation(
    ['upsertRevenueRecognitionOverride', variantId],
    async (payload: UpsertRevenueRecognitionOverrideRequestPayload) => {
      const api = getPrivateApi();
      return api.upsertRevenueRecognitionOverride({
        variantId,
        upsertRevenueRecognitionOverrideRequest: payload,
      });
    },
    {
      ...options,
      onMutate: async (payload) => {
        await queryClient.cancelQueries(customerKey);
        if (contractKey) await queryClient.cancelQueries(contractKey);

        const previousCustomerReport = queryClient.getQueryData<CustomerRevenueReport>(customerKey);
        const previousContractReport = contractKey
          ? queryClient.getQueryData<ContractReport>(contractKey)
          : undefined;

        if (payload.revenueOverride) {
          const patched = applyOptimisticPatch(
            variantId,
            payload.revenueOverride,
            true,
            payload.recognitionTypeOverride ?? null,
            { customer: previousCustomerReport, contract: previousContractReport },
          );
          if (patched.customer) {
            queryClient.setQueryData<CustomerRevenueReport>(customerKey, patched.customer);
          }
          if (patched.contract && contractKey) {
            queryClient.setQueryData<ContractReport>(contractKey, patched.contract);
          }
        }

        const userContext = await options?.onMutate?.(payload);
        return { previousCustomerReport, previousContractReport, userContext } satisfies MutationSnapshot;
      },
      onError: async (err, payload, context) => {
        const ctx = context as MutationSnapshot | undefined;
        if (ctx?.previousCustomerReport) {
          queryClient.setQueryData(customerKey, ctx.previousCustomerReport);
        }
        if (ctx?.previousContractReport && contractKey) {
          queryClient.setQueryData(contractKey, ctx.previousContractReport);
        }
        await options?.onError?.(err, payload, ctx?.userContext);
      },
      onSettled: async (data, error, payload, context) => {
        await queryClient.refetchQueries(customerKey);
        if (contractKey) await queryClient.refetchQueries(contractKey);
        await queryClient.refetchQueries(queryKeys.journalEntriesReport());
        const ctx = context as MutationSnapshot | undefined;
        await options?.onSettled?.(data, error, payload, ctx?.userContext);
      },
    },
  );
}

export function useDeleteRevenueRecognitionOverride(
  variantId: string,
  contractId?: string,
  options?: UseMutationOptions<DeleteRevenueRecognitionOverrideResponsePayload, unknown, void>,
) {
  const queryClient = useQueryClient();
  const customerKey = queryKeys.customerRevenueReport();
  const contractKey = contractId ? queryKeys.contractRevenueScheduleReport(contractId) : null;

  return useMutation(
    ['deleteRevenueRecognitionOverride', variantId],
    async () => {
      const api = getPrivateApi();
      return api.deleteRevenueRecognitionOverride({ variantId });
    },
    {
      ...options,
      onMutate: async (payload) => {
        await queryClient.cancelQueries(customerKey);
        if (contractKey) await queryClient.cancelQueries(contractKey);

        const previousCustomerReport = queryClient.getQueryData<CustomerRevenueReport>(customerKey);
        const previousContractReport = contractKey
          ? queryClient.getQueryData<ContractReport>(contractKey)
          : undefined;

        if (previousCustomerReport) {
          queryClient.setQueryData<CustomerRevenueReport>(
            customerKey,
            patchCustomerReportItemForVariant(previousCustomerReport, variantId, {
              isOverridden: false,
              recognitionTypeOverride: null,
            }),
          );
        }
        if (previousContractReport && contractKey) {
          queryClient.setQueryData<ContractReport>(
            contractKey,
            patchContractReportItemForVariant(previousContractReport, variantId, {
              isOverridden: false,
              recognitionTypeOverride: null,
            }),
          );
        }

        const userContext = await options?.onMutate?.(payload);
        return { previousCustomerReport, previousContractReport, userContext } satisfies MutationSnapshot;
      },
      onError: async (err, payload, context) => {
        const ctx = context as MutationSnapshot | undefined;
        if (ctx?.previousCustomerReport) {
          queryClient.setQueryData(customerKey, ctx.previousCustomerReport);
        }
        if (ctx?.previousContractReport && contractKey) {
          queryClient.setQueryData(contractKey, ctx.previousContractReport);
        }
        await options?.onError?.(err, payload, ctx?.userContext);
      },
      onSettled: async (data, error, payload, context) => {
        await queryClient.refetchQueries(customerKey);
        if (contractKey) await queryClient.refetchQueries(contractKey);
        await queryClient.refetchQueries(queryKeys.journalEntriesReport());
        const ctx = context as MutationSnapshot | undefined;
        await options?.onSettled?.(data, error, payload, ctx?.userContext);
      },
    },
  );
}
